import { useTranslator } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { Loader } from '../../../../__legacy__/components/Loader';
import { GTable, IGColumnProps } from '../../../../__legacy__/components/table/GTable';
import { S_TabsCard } from '../../../../design/card/tabsCard/S_TabsCards';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { IJobAnnotationTool, JOB_ANNOTATION_TYPE } from '../../jobs.model';

interface IProps {
  annotationTools: IJobAnnotationTool[];
  annotationToolIds: string[];
  toggleAnnotationTool: (e: React.ChangeEvent<HTMLInputElement>) => void;
  toggleAllAnnotationTools: () => void;
  isFormDisabled: boolean;
  isLoading: boolean;
}

export const JobAnnotationTools = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const { annotationToolIds, isFormDisabled, toggleAnnotationTool, toggleAllAnnotationTools, annotationTools, isLoading } = props;

  const renderCheckbox = (item: IJobAnnotationTool): React.ReactElement => (
    <S_Checkbox
      checked={annotationToolIds.includes(item.id)}
      onChange={toggleAnnotationTool}
      value={item.id}
      id={item.id}
      disabled={isFormDisabled || !item.compatible || item.required}
      title={item.required ? t('annotation_tool_required') : ''}
    />
  );

  const renderHeaderCheckbox = (): React.ReactElement => (
    <S_Checkbox
      checked={annotationToolIds.length > 0}
      onChange={toggleAllAnnotationTools}
      disabled={isFormDisabled}
      id={'id-select-all'}
      value={'value-select-all'}
    />
  );

  const renderName = (item: IJobAnnotationTool): React.ReactElement => <>{item.name}</>;

  const renderType = (item: IJobAnnotationTool): React.ReactElement => (
    <>{item.tool === JOB_ANNOTATION_TYPE.QUESTION ? t(`${item.type}_question_type`) : t(item.type)}</>
  );

  const renderCompatibility = (item: IJobAnnotationTool): React.ReactElement => <>{item.compatible ? t('yes') : t('no')}</>;

  const renderAnnotationTool = (item: IJobAnnotationTool): React.ReactElement => <>{t(item.tool)}</>;

  const columns = useMemo((): IGColumnProps<IJobAnnotationTool>[] => {
    return [
      {
        headerName: '',
        renderer: renderCheckbox,
        headerRenderer: renderHeaderCheckbox,
      },
      {
        headerName: 'Annotation Tool',
        renderer: renderAnnotationTool,
      },
      {
        headerName: 'Name',
        renderer: renderName,
      },
      {
        headerName: 'Type',
        renderer: renderType,
      },
      {
        headerName: 'Compatible',
        renderer: renderCompatibility,
      },
    ];
  }, [annotationTools, annotationToolIds]);

  const className = 'card-with-list';
  if (isLoading) {
    return (
      <S_TabsCard className={className}>
        <Loader isLoading={true} />
      </S_TabsCard>
    );
  }

  return (
    <S_TabsCard className={className}>
      <h4>{t('select_annotation_tools')}</h4>
      <div>
        <div>
          <GTable
            isLoading={isLoading}
            columns={columns}
            items={annotationTools}
            noItemsPlaceholder={t('no_annotation_tools_in_project')}
          />
        </div>
      </div>
    </S_TabsCard>
  );
});
